import { useState } from 'react';
import { motion } from 'framer-motion';
import Input from './ui/Input';
import { Lock, Mail, Loader } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuthStore } from '../store/authStore';
import backgroundcss from "../css/Login.css";

const ERROR_MESSAGES = {
    google_auth_failed: 'Google sign-in failed. Please try again.',
    server_error: 'Something went wrong. Please try again.',
};

const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const { login, error, isLoading } = useAuthStore();
    const { loginWithRedirect, isLoading: auth0Loading } = useAuth0();
    const [searchParams] = useSearchParams();

    const oauthError = searchParams.get('error');
    const oauthErrorMessage = oauthError
        ? (ERROR_MESSAGES[oauthError] || decodeURIComponent(oauthError))
        : null;

    const handleLogin = async (e) => {
        e.preventDefault();
        try {
            await login(email, password);
        } catch (error) {
            console.log(error);
        }
    };

    const handleGoogleLogin = () => {
        loginWithRedirect({
            authorizationParams: {
                connection: 'google-oauth2',
                redirect_uri: window.location.origin + '/auth/callback',
            },
        });
    };

    return (
        <div className="flex justify-center items-center min-h-screen px-4 py-6 sm:py-12">
            <div className="area">
                <ul className="circles">
                    {Array.from({ length: 10 }).map((_, i) => (<li key={i}></li>))}
                </ul>
            </div>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className='w-full max-w-md bg-gray-800 bg-opacity-50 backdrop-filter backdrop-blur-xl rounded-2xl shadow-xl overflow-hidden'
            >
                <div className='p-6 sm:p-8'>
                    <h2 className='text-2xl sm:text-3xl font-bold font-raleway text-center bg-purple-400 text-transparent bg-clip-text'>
                        Welcome Back
                    </h2>
                    <p className='text-gray-400 text-sm font-raleway sm:text-base text-center mb-6'>
                        Sign in to your account
                    </p>

                    {oauthErrorMessage && (
                        <p className='text-red-500 text-sm font-semibold mb-4 text-center bg-red-500/10 py-2 px-3 rounded-lg'>
                            {oauthErrorMessage}
                        </p>
                    )}

                    {/* ── Google Button ── */}
                    <motion.button
                        onClick={handleGoogleLogin}
                        disabled={auth0Loading}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className='w-full flex items-center justify-center gap-3 py-2.5 px-4 mb-4
                            bg-white text-gray-800 font-semibold rounded-lg shadow-md
                            hover:bg-gray-100 transition duration-200 text-sm sm:text-base
                            disabled:opacity-60 disabled:cursor-not-allowed'
                    >
                        {auth0Loading ? <Loader className="animate-spin size-5" /> : <><GoogleIcon />Continue with Google</>}
                    </motion.button>

                    {/* ── Divider ── */}
                    <div className='flex items-center gap-3 mb-5'>
                        <div className='flex-1 h-px bg-gray-700' />
                        <span className='text-gray-500 text-xs font-libre'>or sign in with email</span>
                        <div className='flex-1 h-px bg-gray-700' />
                    </div>

                    <form onSubmit={handleLogin} className="space-y-6 font-libre">
                        <Input icon={Mail} type='text' placeholder='Email Address' label='Email Address'
                            value={email} onChange={(e) => setEmail(e.target.value)} />
                        <Input icon={Lock} type='password' placeholder='Password' label='Password'
                            value={password} onChange={(e) => setPassword(e.target.value)} />
                        <div className='flex justify-end'>
                            <Link to='/forgot-password' className='text-sm font-libre text-purple-400 hover:underline'>
                                Forgot Password?
                            </Link>
                        </div>
                        {error && <p className='text-red-500 text-sm font-semibold mt-2'>{error}</p>}
                        <motion.button
                            className='w-full py-2.5 sm:py-3 px-4 bg-gradient-to-r from-purple-500 to-indigo-600
                                text-white font-bold rounded-lg shadow-lg hover:from-purple-600 hover:to-indigo-700
                                focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
                                focus:ring-offset-gray-900 transition duration-200 text-sm sm:text-base'
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            type='submit'
                            disabled={isLoading}
                        >
                            {isLoading ? <Loader className="animate-spin mx-auto size-5 sm:size-6" /> : "Sign In"}
                        </motion.button>
                    </form>
                </div>
                <div className='px-6 sm:px-8 py-4 bg-gray-900 bg-opacity-50 flex justify-center'>
                    <p className='text-sm font-libre text-gray-400'>
                        Don't have an account?{' '}
                        <Link to='/signup' className='text-purple-400 hover:underline'>Sign up</Link>
                    </p>
                </div>
            </motion.div>
        </div>
    );
};

// Reusable — import this in SignupPage too
export const GoogleIcon = () => (
    <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 48 48">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
);

export default LoginPage;